import React, { useState } from 'react';
import { m } from 'framer-motion';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiLinkedin,
  FiFacebook,
  FiInstagram,
  FiHeart,
  FiMessageCircle
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { internalUrl, SITE_ORIGIN } from './siteConfig';

const PSI_HOME_REPORT = `https://pagespeed.web.dev/report?url=${encodeURIComponent(`${SITE_ORIGIN}/`)}`;
const GOOGLE_REVIEW_SHARE = 'https://share.google/TY0R7AUo80usJlLRb';

function AccessibilityScoreBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 44"
      width={200}
      height={44}
      className="h-11 w-auto max-w-full shrink-0"
      aria-hidden="true"
    >
      <rect width="200" height="44" rx="8" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
      <circle cx="22" cy="22" r="10" fill="none" stroke="#34d399" strokeWidth={1.5} />
      <path
        fill="none"
        stroke="#34d399"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 22l3 3 6-7"
      />
      <text x="40" y="19" fill="#f8fafc" fontFamily="system-ui,Segoe UI,sans-serif" fontSize={11} fontWeight={600}>
        ACCESSIBILITY
      </text>
      <text x="40" y="33" fill="#94a3b8" fontFamily="system-ui,Segoe UI,sans-serif" fontSize={9.5}>
        Lighthouse · mobile
      </text>
      <text x="188" y="28" fill="#34d399" fontFamily="system-ui,Segoe UI,sans-serif" fontSize={17} fontWeight={700} textAnchor="end">
        98/100
      </text>
    </svg>
  );
}

const Footer = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: internalUrl('home') },
    { name: 'Services', href: internalUrl('services') },
    { name: 'E-commerce & web apps', href: internalUrl('ecommerce-web-apps') },
    { name: 'Portfolio', href: internalUrl('portfolio') },
    { name: 'About', href: internalUrl('about') },
    { name: 'FAQ', href: internalUrl('faq') },
    { name: 'Areas we serve', href: internalUrl('locations') },
    { name: 'Trades & builders', href: internalUrl('trades') },
    { name: 'Guides', href: internalUrl('guides') },
    { name: 'Free SEO audit', href: internalUrl('check-your-seo') },
    { name: 'Keywords explorer', href: internalUrl('keywords-explorer') },
    { name: 'Backlink checker', href: internalUrl('backlink-checker') },
    { name: 'Accessibility scan', href: internalUrl('accessibility-check') },
    { name: 'Website tax calculator', href: internalUrl('tax-calculator') },
    { name: 'Scope fit wizard', href: internalUrl('scope-estimator') },
    { name: 'Reading time helper', href: internalUrl('reading-time-tool') },
    { name: 'Contact', href: internalUrl('contact') },
    { name: 'Client review', href: internalUrl('client-review') }
  ];

  const services = [
    { name: 'Web Development', href: internalUrl('services') },
    { name: 'E-commerce Solutions', href: internalUrl('ecommerce-web-apps') },
    { name: 'Mobile Apps', href: internalUrl('services') },
    { name: 'SEO & Marketing', href: internalUrl('services') },
    { name: 'Maintenance', href: internalUrl('services') }
  ];

  const socialLinks = [
    { icon: FiLinkedin, href: 'https://www.linkedin.com/login', label: 'LinkedIn' },
    { icon: FiFacebook, href: 'https://www.facebook.com/search/top?q=web-cod-craft', label: 'X (Twitter)' },
    {
      icon: FiInstagram,
      href: 'https://www.instagram.com/webcodecraft.paymonthly/',
      label: 'Instagram',
      ariaLabel: 'Instagram @webcodecraft.paymonthly — opens in a new tab'
    }
  ];

  return (
    <>
      <footer id="site-footer" className="bg-gradient-to-b from-slate-950 to-slate-900 text-white border-t border-white/5">
        <div className="container mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-2">
              <m.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center ring-1 ring-white/10">
                    <FiHeart className="text-white text-xl" aria-hidden />
                  </div>
                  <span className="text-2xl font-bold font-display tracking-tight">WebCodeCraft</span>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  South West roots, UK-wide delivery: fast sites, clear content structure, and hosting you do not have to babysit.
                </p>
                <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                  <a
                    href="https://developers.google.com/search/docs/fundamentals/seo-starter-guide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 hover:text-white underline decoration-white/20"
                    aria-label="SEO best practices on Google Search Central (opens in new tab)"
                  >
                    SEO best practices (Google Search Central)
                  </a>
                  {' · '}
                  <a
                    href="https://www.w3.org/WAI/fundamentals/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-300 hover:text-white underline decoration-white/20"
                    aria-label="Web accessibility fundamentals on W3C WAI (opens in new tab)"
                  >
                    Web accessibility fundamentals (W3C WAI)
                  </a>
                </p>
                <a
                  href={PSI_HOME_REPORT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mb-5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  aria-label="Open PageSpeed Insights report for webcodecraft.co.uk showing Lighthouse accessibility score 98 out of 100 (opens in new tab)"
                >
                  <AccessibilityScoreBadge />
                </a>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <m.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
                      aria-label={social.ariaLabel ?? `${social.label} — opens in a new tab`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <social.icon className="text-white" />
                    </m.a>
                  ))}
                </div>
              </m.div>
            </div>

            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-xl font-bold mb-6 text-white">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-xl font-bold mb-6 text-white">Services</h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <a
                      href={service.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {service.name}
                    </a>
                  </li>
                ))}
              </ul>
            </m.div>
          </div>

          <m.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-10 pt-10 border-t border-white/10"
          >
            <h3 id="footer-areas-we-cover" className="text-lg font-bold text-white mb-3">
              Areas we cover
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed mb-3">
              <span className="text-white font-medium">Cornwall: </span>
              <Link to="/locations/truro" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Truro
              </Link>
              {', '}
              <Link to="/locations/falmouth" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Falmouth
              </Link>
              {', '}
              <Link to="/locations/st-austell" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                St Austell
              </Link>
              {', '}
              <Link to="/locations/saltash" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Saltash
              </Link>
              .
            </p>
            <p className="text-sm text-gray-300 leading-relaxed">
              <span className="text-white font-medium">Devon &amp; Torbay: </span>
              <Link to="/locations/exeter" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Exeter
              </Link>
              {', '}
              <Link to="/locations/plymouth" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Plymouth
              </Link>
              {', '}
              <Link to="/locations/torbay" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Torbay
              </Link>
              {', '}
              <Link to="/locations/torquay" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Torquay
              </Link>
              {', '}
              <Link to="/locations/barnstaple" className="text-sky-300 hover:text-white underline-offset-2 hover:underline">
                Barnstaple
              </Link>
              .
            </p>
            <Link
              to="/locations"
              className="inline-block mt-4 text-sm font-medium text-sky-300 hover:text-white underline-offset-2 hover:underline"
            >
              Full locations list &rarr;
            </Link>
          </m.div>

          <m.section
            id="client-review"
            aria-labelledby="client-review-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true, margin: '-40px' }}
            className="border-t border-white/10 mt-12 pt-12 scroll-mt-24"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-6">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-white/10 flex items-center justify-center ring-1 ring-white/15">
                <FiMessageCircle className="text-white text-xl" aria-hidden />
              </div>
              <h2
                id="client-review-heading"
                className="text-xl sm:text-2xl font-bold font-display text-white"
              >
                What clients say
              </h2>
              <span
                className="text-amber-400 text-xl sm:text-2xl leading-none tracking-wide"
                role="img"
                aria-label="5 out of 5 stars"
              >
                ★★★★★
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-10 max-w-5xl">
              <blockquote className="relative pl-4 sm:pl-6 border-l-2 border-sky-400/80 text-gray-200 leading-relaxed space-y-4 text-sm sm:text-base">
                <p>
                  I was needing a website for my new business. I was pretty green to the world of
                  websites, but the whole process was made so simple by Shane as he had all the answers
                  to my questions, all the solutions when we came across little amendments I wanted to
                  make as the site developed. The professional approach and attitude was very impressive.
                </p>
                <p>
                  He put me at ease from the very first call where I attempted to explain my vision of what
                  I wanted. Somehow he has the skills to take nonsense information I fed him and come up
                  with a seriously impressive product that I&apos;m very confident my future clients will use
                  to allow my business to flourish.
                </p>
                <p>Thank you so much Shane.</p>
                <p>
                  Having you behind me takes away so much pressure. I&apos;m absolutely delighted with what you
                  have done for me and the ongoing support.
                </p>
                <footer className="text-slate-400 text-sm pt-2 not-italic">
                  — Client, new business
                </footer>
              </blockquote>
              <blockquote className="relative pl-4 sm:pl-6 border-l-2 border-sky-400/80 text-gray-200 leading-relaxed space-y-4 text-sm sm:text-base">
                <div
                  className="text-amber-400 text-lg leading-none tracking-wide mb-2"
                  role="img"
                  aria-label="5 out of 5 stars"
                >
                  ★★★★★
                </div>
                <p>
                  Really good experience and service with helping me get my website up and going.
                </p>
                <p className="text-slate-400 text-sm pt-1 not-italic">
                  <a
                    href={GOOGLE_REVIEW_SHARE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-300 hover:text-white underline underline-offset-2 font-medium"
                  >
                    Read on Google
                  </a>
                </p>
                <footer className="text-slate-400 text-sm pt-2 not-italic">
                  —{' '}
                  <a
                    href={GOOGLE_REVIEW_SHARE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-300 hover:text-white underline underline-offset-2"
                  >
                    Lewis Jones
                  </a>
                  , Google review
                </footer>
              </blockquote>
            </div>
          </m.section>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="border-t border-gray-700 mt-12 pt-8"
          >
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex items-center">
                <FiMail className="text-slate-400 mr-3 text-xl" />
                <div>
                  <p className="font-semibold text-white">Email</p>
                  <p className="text-gray-300">hello@webcodecraft.info</p>
                  <p className="text-gray-300">accounts@webcodecraft.co.uk</p>
                </div>
              </div>
              <div className="flex items-center">
                <FiPhone className="text-slate-400 mr-3 text-xl" />
                <div>
                  <p className="font-semibold text-white">Phone</p>
                  <p className="text-gray-300">+447864841709</p>
                </div>
              </div>
              <div className="flex items-center">
                <FiMapPin className="text-slate-400 mr-3 text-xl" />
                <div>
                  <p className="font-semibold text-white">Location</p>
                  <p className="text-gray-300">Devon UK</p>
                </div>
              </div>
            </div>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="border-t border-gray-700 mt-8 pt-8 flex flex-col lg:flex-row justify-between items-center gap-6"
          >
            <div className="text-gray-300 text-sm text-center lg:text-left order-2 lg:order-1">
              © {currentYear} WebCodeCraft. All rights reserved.
            </div>
            <a
              href="https://www.marketingquotes.co.uk/preview/url/directory/preview/url/11211"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-col items-center justify-center gap-0 text-gray-300 hover:text-white text-xs transition-colors order-1 lg:order-2 mx-4 md:mx-12 align-middle"
              aria-label="WebCodeCraft on Marketing Quotes (opens in new tab)"
            >
              <img
                src="https://www.marketingquotes.co.uk/wp-content/uploads/2021/07/logo.svg"
                alt="Marketing Quotes"
                width={45}
                height={50}
                loading="lazy"
                decoding="async"
                className="block align-middle"
              />
              <span className="mt-1">webcodecraft</span>
            </a>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm order-3">
              <button
                onClick={() => setShowPrivacyModal(true)}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setShowCookieModal(true)}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Cookie Policy
              </button>
              <button
                onClick={() => setShowTermsModal(true)}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </m.div>
        </div>
      </footer>

      {showPrivacyModal && (
        <div className="modal-overlay show" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Privacy Policy</h2>
            <div className="space-y-4 text-sm">
              <p>
                <span className="font-semibold text-gray-900">Last updated:</span> {currentYear}
              </p>
              <p>
                WebCodeCraft ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, and safeguard your information 
                when you visit our website or use our services.
              </p>
              <h3 className="font-bold text-lg">Information We Collect</h3>
              <p>
                We collect information you provide directly to us, such as when you fill out 
                our contact form, including your name, email address, phone number, and project details.
              </p>
              <h3 className="font-bold text-lg">How We Use Your Information</h3>
              <p>
                We use the information we collect to provide our services, communicate with you, 
                and improve our website and services.
              </p>
              <h3 className="font-bold text-lg">Information Sharing</h3>
              <p>
                We do not sell, trade, or otherwise transfer your personal information to third 
                parties without your consent, except as described in this policy.
              </p>
              <h3 className="font-bold text-lg">Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy, please contact us at 
                privacy@webcodecraft.com.
              </p>
            </div>
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="btn btn-primary mt-6"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showCookieModal && (
        <div className="modal-overlay show" onClick={() => setShowCookieModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Cookie Policy</h2>
            <div className="space-y-4 text-sm">
              <p>
                <span className="font-semibold text-gray-900">Last updated:</span> {currentYear}
              </p>
              <p>
                This Cookie Policy explains how WebCodeCraft uses cookies and similar technologies 
                to recognize you when you visit our website.
              </p>
              <h3 className="font-bold text-lg">What Are Cookies</h3>
              <p>
                Cookies are small data files that are placed on your computer or mobile device 
                when you visit a website. They are widely used to make websites work more 
                efficiently and provide information to website owners.
              </p>
              <h3 className="font-bold text-lg">How We Use Cookies</h3>
              <p>
                We use cookies for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand how visitors use our site</li>
                <li>Marketing cookies to provide relevant content</li>
              </ul>
              <h3 className="font-bold text-lg">Managing Cookies</h3>
              <p>
                You can control and manage cookies through your browser settings. However, 
                disabling certain cookies may affect the functionality of our website.
              </p>
            </div>
            <button
              onClick={() => setShowCookieModal(false)}
              className="btn btn-primary mt-6"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="modal-overlay show" onClick={() => setShowTermsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Terms of Service</h2>
            <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto">
              <p><span className="font-semibold text-gray-900">Last updated:</span> {currentYear}</p>
              <h3 className="font-bold text-lg">1. Acceptance of Terms</h3>
              <p>By accessing or using the WebCodeCraft website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, please do not use our site or services.</p>
              <h3 className="font-bold text-lg">2. Services</h3>
              <p>WebCodeCraft provides web development and related digital services. We reserve the right to modify or discontinue any service at any time without notice.</p>
              <h3 className="font-bold text-lg">3. User Responsibilities</h3>
              <ul className="list-disc ml-6">
                <li>You agree to provide accurate and complete information when engaging our services.</li>
                <li>You are responsible for maintaining the confidentiality of your account information.</li>
                <li>You agree not to use our services for any unlawful or prohibited purpose.</li>
              </ul>
              <h3 className="font-bold text-lg">4. Intellectual Property</h3>
              <p>All content, designs, and code provided by WebCodeCraft are the intellectual property of WebCodeCraft or its licensors. You may not reproduce, distribute, or create derivative works without our written permission.</p>
              <h3 className="font-bold text-lg">5. Payment & Refunds</h3>
              <p>Payment terms will be outlined in your project proposal or invoice. Refunds are provided at our discretion and only in accordance with the terms agreed upon in writing.</p>
              <h3 className="font-bold text-lg">6. Limitation of Liability</h3>
              <p>WebCodeCraft is not liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services. Our total liability is limited to the amount paid for the services.</p>
              <h3 className="font-bold text-lg">7. Third-Party Links</h3>
              <p>Our website may contain links to third-party sites. We are not responsible for the content or practices of these sites.</p>
              <h3 className="font-bold text-lg">8. Changes to Terms</h3>
              <p>We may update these Terms of Service at any time. Changes will be posted on this page with an updated effective date.</p>
              <h3 className="font-bold text-lg">9. Contact</h3>
              <p>If you have any questions about these Terms, please contact us at hello@webcodecraft.info.</p>
            </div>
            <button
              onClick={() => setShowTermsModal(false)}
              className="btn btn-primary mt-6"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer; 